// Base button. Three variants: primary (black), outline (black outline), ghost (text only).
// All sizes target >=44px touch target. See DESIGN.md §6.

import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "outline" | "ghost" | "danger" | "warning";
type Size = "md" | "lg";

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  children: ReactNode;
};

const baseStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "var(--space-2)",
  borderRadius: "var(--radius-md)",
  fontWeight: 600,
  fontSize: "var(--font-size-15)",
  minHeight: 44,
  padding: "0 var(--space-4)",
  transition: "background 120ms, color 120ms, transform 80ms",
  userSelect: "none",
};

const sizeStyle: Record<Size, React.CSSProperties> = {
  md: { minHeight: 44, padding: "0 var(--space-4)" },
  lg: { minHeight: 48, padding: "0 var(--space-5)", fontSize: "var(--font-size-16)" },
};

const variantStyle: Record<Variant, React.CSSProperties> = {
  primary: { background: "var(--color-primary)", color: "var(--text-inverse)", border: "1px solid var(--color-primary)" },
  outline: { background: "var(--surface-raised)", color: "var(--text-brand)", border: "1px solid var(--border-default)" },
  ghost: { background: "transparent", color: "var(--text-primary)", border: "1px solid transparent" },
  danger: { background: "var(--color-danger)", color: "var(--text-inverse)", border: "1px solid var(--color-danger)" },
  warning: { background: "var(--color-warning)", color: "var(--text-inverse)", border: "1px solid var(--color-warning)" },
};

export function Button({
  variant = "primary",
  size = "md",
  fullWidth,
  style,
  disabled,
  children,
  ...rest
}: Props) {
  return (
    <button
      {...rest}
      disabled={disabled}
      style={{
        ...baseStyle,
        ...sizeStyle[size],
        ...variantStyle[variant],
        width: fullWidth ? "100%" : undefined,
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        ...style,
      }}
    >
      {children}
    </button>
  );
}
